/**
 * Early access changes which releases this computer receives. The switch keeps
 * the saved preference authoritative, and the updater owns live progress.
 * Channel-change notices retain any discarded download or cancelled check so
 * an operator can understand what happened to work already underway.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Info, LoaderCircle, RefreshCw } from 'lucide-react';

import type { UpdateChannel, UpdateChannelSetting, UpdateStatus } from '../shared/desktop-api';
import { updateActivityLine } from '../shared/update-channels';

import { TooltipButton } from './TooltipButton';

const desktop = () => window.capturePointDesktop;

export function SoftwareUpdatesCard({
  appVersion = '',
  onCheckForUpdate,
  checking = false,
  checkNote = null,
}: {
  appVersion?: string;
  /**
   * Ask for an update now.
   *
   * This used to be the click target on a 10px version chip at the foot of the
   * rail — the product's only discoverable update control, at 2.51:1 contrast,
   * labelled with a version number rather than with what pressing it did, and
   * hidden entirely below 1100px. Here it is a button that says its own name.
   */
  onCheckForUpdate?: () => void;
  checking?: boolean;
  checkNote?: string | null;
} = {}) {
  const [setting, setSetting] = useState<UpdateChannelSetting | null>(null);
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [settingFailure, setSettingFailure] = useState<string | null>(null);
  const statusRevision = useRef(0);

  const loadSetting = useCallback(() => {
    const api = desktop();
    if (!api) {
      setSettingFailure('Paige could not read the update channel on this computer.');
      return;
    }
    setSettingFailure(null);
    void api
      .getUpdateChannel()
      .then(setSetting)
      .catch((reason: unknown) =>
        setSettingFailure(
          reason instanceof Error
            ? reason.message
            : 'Paige could not read the update channel on this computer.',
        ),
      );
  }, []);

  useEffect(() => {
    let current = true;
    const api = desktop();
    const unsubscribe = api?.onUpdateStatusChanged((next) => {
      if (!current) return;
      statusRevision.current++;
      setStatus(next);
    });
    const request = statusRevision.current;
    loadSetting();
    void api
      ?.getUpdateStatus()
      .then((next) => {
        if (current && statusRevision.current === request) setStatus(next);
      })
      .catch(() => undefined);
    // The switch starts a check that finishes later; this is how its outcome
    // arrives, and it is why the line below keeps changing without a reload.
    return () => {
      current = false;
      unsubscribe?.();
    };
  }, [loadSetting]);

  const choose = (channel: UpdateChannel) => {
    if (changing || !setting || channel === setting.channel) return;
    setChanging(true);
    setFailure(null);
    const request = statusRevision.current;
    void desktop()
      ?.setUpdateChannel(channel)
      .then((result) => {
        setSetting((current) => (current ? { ...current, channel: result.channel } : current));
        setNotice(result.notice);
        // A newer event can finish the check before this IPC reply arrives.
        // Neither this reply nor the initial read may revive its old activity.
        if (statusRevision.current === request) {
          statusRevision.current++;
          setStatus(result.status);
        }
      })
      .catch((reason: unknown) => {
        // The stored setting is the truth. If the change did not land, the switch
        // must go back rather than showing a channel this workstation is not on.
        setFailure(reason instanceof Error ? reason.message : 'The channel could not be changed.');
        void desktop()
          ?.getUpdateChannel()
          .then(setSetting)
          .catch(() => undefined);
      })
      .finally(() => setChanging(false));
  };

  // A development build cannot apply an update, but its channel is still a
  // real, persistent setting. Hiding the preference in that state made the release
  // channel literally undiscoverable during UI review. Only the check/install
  // action is unavailable; the choice remains visible and editable.
  const disabledReason = status?.state === 'disabled' ? status.reason : null;
  const isChecking = status?.state === 'checking';
  // A manual check can return while a replacement channel check is running.
  // Its saved note must not preserve that activity after the replacement ends.
  const retainedCheckNote =
    checkNote === updateActivityLine({ state: 'checking' }) ? null : checkNote;

  const descriptionId = useId();
  const earlyAccess = setting?.channel === 'beta';
  const busyUpdate = isChecking || status?.state === 'downloading';
  return (
    <section className="card software-updates">
      <div className="updates-summary">
        <svg
          className={`updates-status-art${busyUpdate ? ' is-active' : ''}`}
          viewBox="0 0 80 80"
          aria-hidden="true"
          focusable="false"
        >
          <rect x="22" y="25" width="36" height="26" rx="4" />
          <path d="M32 58 H48 M40 51 V58" />
          {!busyUpdate && status?.state === 'idle' && (
            <path className="updates-status-check" d="M31 38 L37 44 L49 32" />
          )}
          <g className="updates-status-orbit">
            <path d="M12 29 A30 30 0 0 1 66 21 L66 12 M66 21 H57 M68 51 A30 30 0 0 1 14 59 L14 68 M14 59 H23" />
          </g>
        </svg>
        <div className="updates-summary-copy">
          <h3>
            Paige <strong>{appVersion || 'Unavailable'}</strong>
          </h3>
          <p className="updates-automatic">
            {disabledReason
              ? 'Automatic updates are unavailable.'
              : status
                ? 'Automatic updates are on.'
                : 'Loading update status…'}
          </p>
          <p className="software-updates-status" role="status">
            {status?.state === 'idle'
              ? 'You’re up to date.'
              : status
                ? updateActivityLine(status)
                : null}
          </p>
          {onCheckForUpdate && !disabledReason && (
            <div className="software-updates-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={onCheckForUpdate}
                disabled={checking || isChecking || !status}
              >
                <RefreshCw size={14} />
                {isChecking ? 'Checking…' : 'Check for updates'}
              </button>
              {retainedCheckNote && (
                <span className="software-updates-check-note" role="status">
                  {retainedCheckNote}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="early-access-choice">
        {settingFailure ? (
          <div
            className="software-updates-setting-failure"
            role="alert"
            data-telemetry="error:update-channel-load-failed"
          >
            <span>{settingFailure}</span>
            <button type="button" className="quiet-button small" onClick={loadSetting}>
              Try again
            </button>
          </div>
        ) : !setting ? (
          <p className="software-updates-waiting">
            <LoaderCircle className="spin" size={16} />
            Loading your preference…
          </p>
        ) : (
          <>
            <div className="early-access-row">
              <label className="early-access-toggle">
                <span>
                  <strong>Try new features first</strong>
                  <small id={descriptionId}>
                    {changing ? 'Saving…' : 'Get early releases while they’re still being refined.'}
                  </small>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Try new features first"
                  aria-describedby={descriptionId}
                  checked={earlyAccess}
                  disabled={changing}
                  onChange={(event) => choose(event.target.checked ? 'beta' : 'stable')}
                />
                <span className="early-access-switch" aria-hidden="true" />
              </label>
              <TooltipButton
                type="button"
                className="icon-button"
                helpOnClick
                aria-label="About early access"
                tooltip="Early releases may have bugs."
              >
                <Info size={17} />
              </TooltipButton>
            </div>
          </>
        )}
        {notice && (
          <p className="software-updates-notice" role="status">
            {notice}
          </p>
        )}
        {failure && (
          <p
            className="software-updates-failure"
            role="alert"
            data-telemetry="error:update-channel-save-failed"
          >
            {failure}
          </p>
        )}
      </div>
    </section>
  );
}
